// Shared insight rubric — the single contract every insight generator
// composes against, so practitioner- and patient-facing text stops
// drifting apart.
//
// Every insight has three parts, in order:
//   OBSERVED — the concrete data fact ("HRV dropped 3 of the last 4 Mondays")
//   PATTERN  — the correlation that makes it matter ("each drop lined up
//              with a journal entry about work")
//   HOOK     — audience-dependent close:
//              practitioner → a ranked, actionable correlation or flag
//                (directive-adjacent is fine; the practitioner interprets)
//              patient      → a question or integration prompt, never a
//                directive. Allowed modes: Reflect / Question /
//                Contextualize. "You should…" always fails.
//
// The lint kills hedging preambles, restated scores the reader already
// sees on the dial, and clinical Latinate vocabulary (Body/Mind/Behavior
// only — see domainLabels.js for the canonical DOMAIN_WORD map). The
// actionable test: an insight passes only when all three parts are
// present and the hook gives the reader something to DO or ASK in one
// read — a bare number restatement is a lint failure, not an insight.
//
// Mirrors app/services/insight_rubric.py: violation codes, rule lists,
// and pass/fail behavior MUST stay in sync between the two files.

export const PRACTITIONER = "practitioner";
export const PATIENT = "patient";

// Violation codes (shared contract with insight_rubric.py — do not
// rename on one side only)
export const MISSING_PART = "MISSING_PART";
export const HEDGING_PREAMBLE = "HEDGING_PREAMBLE";
export const SCORE_RESTATEMENT = "SCORE_RESTATEMENT";
export const CLINICAL_VOCAB = "CLINICAL_VOCAB";
export const PATIENT_DIRECTIVE = "PATIENT_DIRECTIVE";
export const PATIENT_HOOK_NOT_REFLECTIVE = "PATIENT_HOOK_NOT_REFLECTIVE";
export const NUMERIC_CLAIM_MISMATCH = "NUMERIC_CLAIM_MISMATCH";
export const PHYSIOLOGICAL_CLAIM = "PHYSIOLOGICAL_CLAIM";

// Hedging preambles — filler that delays the observation. Checked at the
// start of the insight and at the start of every sentence.
const HEDGING_PREAMBLES = [
  "it seems",
  "it looks like",
  "it appears",
  "it might be worth",
  "it may be worth",
  "it's worth noting",
  "it is worth noting",
  "worth noting that",
  "note that",
  "just a note",
  "just a reminder",
  "as a reminder",
  "keep in mind",
  "you may have noticed",
];

// Clinical / Latinate vocabulary. Display vocabulary is Body / Mind /
// Behavior; the adjective forms and internal domain codes must never
// reach output.
const CLINICAL_ADJECTIVES =
  /\b(biological|physiological|psychological|behavioral|behavioural|psych)\b/i;
const DOMAIN_CODES = /\b(BIO|PSYCH|BEH|RCI)\b/; // case-sensitive

// Score restatement — the reader already sees the number on the dial.
// Data facts with numbers are fine ("dropped 3 of the last 4 Mondays");
// naming a score/signal value is not ("Your Body score is 62").
const SCORE_RESTATEMENT_RE =
  /\b(score|signal|total|average|body|mind|behavior)\b[^.?!]{0,40}?\b(is|was|sits?|stands?|remains?|holding|averaging|hit|reached)\b[^.?!]{0,12}?\b\d{1,3}\b/i;
const SCORE_OF_RE = /\bscore of \d{1,3}\b/i;

// Patient-side directive language. Allowed patient modes are Reflect /
// Question / Contextualize — anything that tells the patient what to do
// fails. ("try" fails outright; imperatives are caught sentence-initially
// so narrative uses like "your streak stopped" stay legal.)
const DIRECTIVE_SECOND_PERSON =
  /\byou (should|need to|must|ought to|have to|might want to)\b/i;
const DIRECTIVE_TRY = /\btry\b/i;
const DIRECTIVE_START_STOP = /\b(start|stop|begin|quit)\s+\w+ing\b/i;
const DIRECTIVE_IMPERATIVE =
  /(?:^|[.!?]\s+)(try|start|stop|do|don't|prioritize|prioritise|make|take|add|avoid|focus|aim|consider|schedule|set|get|keep)\b/i;

// Patient hook must land in Reflect / Question / Contextualize mode. A
// question mark always qualifies; otherwise the hook must carry a
// reflective or contextualizing frame. Heuristic floor, not a ceiling —
// extend the list before loosening the rule.
const REFLECTIVE_MARKERS = [
  "what do you",
  "make of",
  "notice",
  "worth sitting with",
  "that's not",
  "that is not",
  "that's the same",
  "lines up",
  "lined up",
];

// Numeric fidelity — completed-vs-skipped gap claims. Generators that
// hold the computed numbers opt in via lintGapClaims(); it is NOT part
// of evaluateInsight(), which stays a pure-text contract. Attribution
// is CLAUSE-scoped, not sentence-scoped: narrative sentences routinely
// carry both the trajectory figure and a completed-vs-skipped mention,
// so a figure counts as a gap claim only when the comparative
// construction sits in its own clause. Mirrors lint_gap_claims in
// insight_rubric.py.
const GAP_COMPLETED_TERM = /\b(complet\w*|done)\b/i;
const GAP_SKIPPED_TERM = /\b(skip\w*|miss\w*)\b/i;
const GAP_SIDE_TERM = /\b(complet\w*|done|skip\w*|miss\w*)\b/i;
const GAP_COMPARATIVE =
  /\b(higher|lower|above|below|over|under|versus|vs\.?|than|compared)\b/i;
const GAP_WORD = /\b(gap|difference|spread)\b/i;
const NUMBER_WORDS = {
  one: 1, two: 2, three: 3, four: 4, five: 5,
  six: 6, seven: 7, eight: 8, nine: 9, ten: 10,
  eleven: 11, twelve: 12, thirteen: 13, fourteen: 14,
  fifteen: 15, sixteen: 16, seventeen: 17, eighteen: 18,
  nineteen: 19, twenty: 20,
};
const GAP_CLAIM = new RegExp(
  "(?:([+-]?\\d{1,3}(?:\\.\\d+)?)|\\b(" +
    Object.keys(NUMBER_WORDS).join("|") +
    ")\\b)\\s*-?\\s*(?:percentage\\s+)?(?:point|pt)s?\\b" +
    "|([+-]?\\d{1,3}(?:\\.\\d+)?)\\s*(?:%|percent\\b)",
  "gi",
);
const GAP_DIR_NEG = /\b(lower|below|under|beneath|less)\b/i;
const GAP_DIR_POS = /\b(higher|above|over|greater|more)\b/i;
const CLAUSE_BOUNDARY = /[,;:]|—|–|\s-\s/g;

function splitSentences(text) {
  return text
    .split(/(?<=[.!?])\s+/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function clauseAfter(sentence, pos, maxLen = 80) {
  const window = sentence.slice(pos, pos + maxLen);
  const cut = window.match(/[,;:]|—|–|\s-\s/);
  return cut ? window.slice(0, cut.index) : window;
}

function clauseBefore(sentence, pos, maxLen = 80) {
  const window = sentence.slice(Math.max(0, pos - maxLen), pos);
  const cuts = [...window.matchAll(CLAUSE_BOUNDARY)];
  if (!cuts.length) return window;
  const last = cuts[cuts.length - 1];
  return window.slice(last.index + last[0].length);
}

// Check every clause-attributed completed-vs-skipped point/percent
// claim in text against the computed gap
// ((completed_avg - skipped_avg) × 100, signed). actualGapPoints=null
// means the data holds no such comparison, so any attributed figure is
// a fabrication. Heuristic floor, not a ceiling — a figure whose clause
// doesn't clearly name the comparison goes unchecked.
export function lintGapClaims(text, actualGapPoints, tolerance = 4.0, part = "insight") {
  const violations = [];
  for (const sentence of splitSentences(text)) {
    if (!GAP_COMPLETED_TERM.test(sentence) || !GAP_SKIPPED_TERM.test(sentence)) continue;
    for (const m of sentence.matchAll(GAP_CLAIM)) {
      const raw = m[1] || m[3];
      const claimed = raw ? parseFloat(raw) : NUMBER_WORDS[m[2].toLowerCase()];
      const trailing = clauseAfter(sentence, m.index + m[0].length);
      const preceding = clauseBefore(sentence, m.index);
      // Clause-scoped attribution: the comparison must be stated in the
      // figure's own clause, not merely elsewhere in the sentence.
      const attributed =
        (GAP_COMPARATIVE.test(trailing) && GAP_SIDE_TERM.test(trailing)) ||
        (GAP_WORD.test(preceding) &&
          GAP_COMPLETED_TERM.test(preceding) &&
          GAP_SKIPPED_TERM.test(preceding));
      if (!attributed) continue;
      if (actualGapPoints === null || actualGapPoints === undefined) {
        violations.push({
          code: NUMERIC_CLAIM_MISMATCH,
          message:
            `Claims a ${claimed}-point completed-vs-skipped gap, but the ` +
            "data holds no completed-vs-skipped comparison — drop the figure.",
          part,
        });
        continue;
      }
      let signed = null;
      if (raw && /^[+-]/.test(raw.trim())) signed = claimed;
      else if (GAP_DIR_NEG.test(trailing)) signed = -Math.abs(claimed);
      else if (GAP_DIR_POS.test(trailing)) signed = Math.abs(claimed);
      const mismatch =
        signed !== null
          ? Math.abs(signed - actualGapPoints) > tolerance
          : Math.abs(Math.abs(claimed) - Math.abs(actualGapPoints)) > tolerance;
      if (mismatch) {
        violations.push({
          code: NUMERIC_CLAIM_MISMATCH,
          message:
            `Claims a ${claimed}-point completed-vs-skipped gap; the computed ` +
            `gap is ${actualGapPoints >= 0 ? "+" : ""}${actualGapPoints.toFixed(1)} points — ` +
            "quote the computed figure or describe the direction without a number.",
          part,
        });
      }
    }
  }
  return violations;
}

function sentenceStarts(text) {
  return text
    .split(/[.!?]\s+/)
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);
}

// Physiological-claim guard (App Store Guideline 1.4.1). The model only
// ever sees derived labels — never raw HRV/sleep/RHR values — so the
// failure mode is QUALITATIVE: asserting what a biometric means
// ("higher HRV means your body is recovering well") or narrating body
// systems the data cannot see. Descriptive metric mentions stay legal
// ("HRV steadier this week"); the metric rule requires an interpretive
// or normative predicate in the same sentence. Mirrors
// lint_physiological_claims in insight_rubric.py.
const PHYS_SYSTEM =
  /\b(nervous\s+system|autonomic|parasympathetic|sympathetic|vagal|cortisol|adrenaline|melatonin|hormon\w*|immune|immunity|inflammat\w*|cardiovascular|blood\s+pressure|metabol\w*|circadian)\b/i;
const PHYS_VERDICT =
  /\b(diagnos\w*|symptom\w*|disease|illness|disorder|longevity|life\s+expectancy|health\s+risk|risk\s+of)\b/i;
const PHYS_METRIC =
  /\b(hrv|heart[\s-]rate\s+variability|heart[\s-]?rate|resting\s+hr|bpm|pulse|readiness|sleep|respiratory\s+rate|spo2|oxygen)\b/i;
const PHYS_INTERPRETIVE =
  /\b(means?|meaning|indicat\w*|sign\s+of|suggest\w*|reflect\w*|points?\s+to|linked\s+to|tied\s+to|associated\s+with|translates?\s+to|drives?|caus\w*|leads?\s+to|improv\w*|boost\w*|strengthen\w*|support\w*|protect\w*|lowers?|raises?|reduc\w*|increases?|helps?)\b/i;
// Normative verdicts need a copula ("your heart rate looks good") —
// bare comparatives over the user's own days ("better sleep on protocol
// days") are data description, not a health verdict.
const PHYS_NORMATIVE =
  /\b(is|are|was|were|looks?|looking|seems?|seemed|remains?|stays?|staying)\s+(?:really\s+|quite\s+|very\s+)?(good|great|bad|better|best|worse|worst|healthy|unhealthy|ideal|optimal|poor|excellent)\b/i;

// Hedged causal language. The three rules above were tuned on ≤25-word
// insights, where a mechanism claim had to be blunt to fit; chat is
// multi-paragraph prose, and the same claim arrives hedged instead —
// "tends to", "usually", "the others follow" — with no word from
// PHYS_INTERPRETIVE anywhere in it. Haiven's doctrine does not ban
// relating two things, it bans relating them as MECHANISM: "often
// followed by" and "tends to line up with" are the correct forms, so
// co-occurrence language is protected by CORRELATIONAL_SAFE.
// "pulling on/at" and "weigh on / weigh down (on)" are in the list.
// They were held out when this rule first landed, because two pieces of
// shipped rubric-gated Haiven copy used them; that copy has since been
// rewritten to co-occurrence framing (insight_engine's scattered-check-in
// fallback, and RC_SEVERE_DOMAIN_CAP in DomainDetail.jsx), so the
// carve-out is gone. "move/track together" stays out on purpose — bare
// co-occurrence is what the doctrine asks for, and it is caught only as
// a generalised law by HEDGED_LAW + DEPENDENCY_CONNECTIVE.
// Mirrors _CAUSAL_* in insight_rubric.py.
const CAUSAL_MECHANISM =
  /\b(drag\w*\s+(?:on|down)|(?:creat|caus|produc|ad)\w*\s+(?:a\s+|some\s+)?drag|spill\w*\s+(?:over|into)|ripple\w*\s+(?:out|into|through)|knock-?on\s+effect|hand\s+in\s+hand|leading\s+(?:condition|domain)|pull(?:s|ed|ing)?\s+(?:on|at)|weigh(?:s|ed|ing)?\s+(?:down\s+)?(?:on|upon)|weigh(?:s|ed|ing)?\s+down)\b/i;
const CAUSAL_PROPAGATION =
  /\bthe\s+(?:other|others|rest|two)\b[^.?!]{0,30}?\bfollows?\b/i;
const HEDGED_LAW =
  /\b(tends?\s+to|tending\s+to|usually|typically|generally|commonly|normally|more\s+often\s+than\s+not|as\s+a\s+rule)\b/i;
const DEPENDENCY_CONNECTIVE =
  /\b(when|whenever|because|as\s+a\s+result|which\s+is\s+why|that'?s\s+why|so\s+that)\b/i;
const CORRELATIONAL_SAFE =
  /\b(lin(?:e|es|ed|ing)\s+up\s+with|follow(?:ed|ing)\s+by|coincid\w*|alongside|at\s+the\s+same\s+time|in\s+the\s+same\s+(?:week|window|stretch|day|days)|overlap\w*|track\w*\s+with|show\w*\s+up\s+(?:together|alongside)|pair\w*\s+with|side\s+by\s+side)\b/i;

// Flag assertions of physiological/medical fact in patient text. Five
// rules: body-system/mechanism vocabulary (any occurrence), medical-
// verdict vocabulary (any occurrence), a named biometric carrying an
// interpretive or normative predicate in the same sentence, causal-force
// / cross-domain-propagation vocabulary (any occurrence), and a hedged
// generalised law stated with a dependency connective.
export function lintPhysiologicalClaims(text, part = "insight") {
  const violations = [];
  const system = text.match(PHYS_SYSTEM);
  if (system) {
    violations.push({
      code: PHYSIOLOGICAL_CLAIM,
      message:
        `Physiological mechanism language "${system[0]}" — describe the ` +
        "user's own data, never body systems the data cannot see.",
      part,
    });
  }
  const verdict = text.match(PHYS_VERDICT);
  if (verdict) {
    violations.push({
      code: PHYSIOLOGICAL_CLAIM,
      message:
        `Medical framing "${verdict[0]}" — Haiven measures conditions, ` +
        "not outcomes; no diagnosis-adjacent language.",
      part,
    });
  }
  const mechanism = text.match(CAUSAL_MECHANISM) || text.match(CAUSAL_PROPAGATION);
  if (mechanism) {
    violations.push({
      code: PHYSIOLOGICAL_CLAIM,
      message:
        `Causal-mechanism language "${mechanism[0].trim()}" — say what the ` +
        "data did next to what, never what one thing does to another " +
        "('often followed by', 'tends to line up with').",
      part,
    });
  }
  for (const sentence of splitSentences(text)) {
    const metric = sentence.match(PHYS_METRIC);
    if (metric && (PHYS_INTERPRETIVE.test(sentence) || PHYS_NORMATIVE.test(sentence))) {
      violations.push({
        code: PHYSIOLOGICAL_CLAIM,
        message:
          `Interprets biometric "${metric[0]}" as a health claim — ` +
          "state what the data did, not what it means physiologically.",
        part,
      });
    }
    // Questions are exempt: the doctrine asks for open questions, and
    // asking one is not asserting a mechanism.
    if (sentence.trimEnd().endsWith("?")) continue;
    if (CORRELATIONAL_SAFE.test(sentence)) continue;
    const law = sentence.match(HEDGED_LAW);
    const dep = sentence.match(DEPENDENCY_CONNECTIVE);
    if (law && dep) {
      violations.push({
        code: PHYSIOLOGICAL_CLAIM,
        message:
          `Hedged causal claim "${law[0]} … ${dep[0]}" — a generalised rule ` +
          "about what happens when something else happens is a mechanism " +
          "claim with a hedge on it. State what this person's own data did, " +
          "in the order it did it.",
        part,
      });
    }
  }
  return violations;
}

// Lint one span of insight text for the given audience. Structural
// checks (three-part shape, hook mode) live in evaluateInsight(); this
// covers the vocabulary/tone rules that apply to any insight string.
export function lintText(text, audience, part = "insight") {
  const violations = [];

  for (const start of sentenceStarts(text)) {
    const preamble = HEDGING_PREAMBLES.find((p) => start.startsWith(p));
    if (preamble) {
      violations.push({
        code: HEDGING_PREAMBLE,
        message: `Hedging preamble "${preamble}" — open with the observation instead.`,
        part,
      });
    }
  }

  const clinical = text.match(CLINICAL_ADJECTIVES) || text.match(DOMAIN_CODES);
  if (clinical) {
    violations.push({
      code: CLINICAL_VOCAB,
      message: `Clinical vocabulary "${clinical[0]}" — output uses Body/Mind/Behavior only.`,
      part,
    });
  }

  if (SCORE_RESTATEMENT_RE.test(text) || SCORE_OF_RE.test(text)) {
    violations.push({
      code: SCORE_RESTATEMENT,
      message:
        "Restates a score the reader already sees on the dial — cut it or " +
        "replace it with the concrete data fact behind the number.",
      part,
    });
  }

  if (audience === PATIENT) {
    const directive =
      text.match(DIRECTIVE_SECOND_PERSON) ||
      text.match(DIRECTIVE_TRY) ||
      text.match(DIRECTIVE_START_STOP) ||
      text.match(DIRECTIVE_IMPERATIVE);
    if (directive) {
      violations.push({
        code: PATIENT_DIRECTIVE,
        message:
          `Directive language "${directive[0].trim()}" — patient insights may only ` +
          "Reflect, Question, or Contextualize, never direct.",
        part,
      });
    }
    // Patient-only: practitioner narratives may interpret biometrics
    // (that surface has its own grounded-figure lints); patient text
    // asserting physiological fact is the Guideline 1.4.1 exposure.
    violations.push(...lintPhysiologicalClaims(text, part));
  }

  return violations;
}

function hookIsReflective(hook) {
  if (hook.includes("?")) return true;
  const lowered = hook.toLowerCase();
  return REFLECTIVE_MARKERS.some((marker) => lowered.includes(marker));
}

// Assemble OBSERVED + PATTERN + HOOK and run the full rubric. Returns
// { text, violations, passes }; passes is true only when every part is
// present, every lint rule clears, and the hook lands in the right mode
// for the audience (patient hooks must be a question or a reflective/
// contextualizing prompt). Never throws on content — callers gate on
// passes and log violations.
export function evaluateInsight({ observed, pattern, hook, audience }) {
  if (audience !== PRACTITIONER && audience !== PATIENT) {
    throw new Error(`unknown audience: ${audience}`);
  }

  const violations = [];
  const parts = { observed, pattern, hook };

  for (const [name, value] of Object.entries(parts)) {
    if (!(value || "").trim()) {
      violations.push({
        code: MISSING_PART,
        message:
          `Missing ${name.toUpperCase()} — every insight needs the three-part ` +
          "shape (observed fact, pattern, hook) to be actionable in one read.",
        part: name,
      });
    }
  }

  for (const [name, value] of Object.entries(parts)) {
    if ((value || "").trim()) {
      violations.push(...lintText(value, audience, name));
    }
  }

  if (audience === PATIENT && (hook || "").trim() && !hookIsReflective(hook)) {
    violations.push({
      code: PATIENT_HOOK_NOT_REFLECTIVE,
      message:
        "Patient hook must Reflect, Question, or Contextualize — ask " +
        'something ("what do you make of that?") or frame the pattern, ' +
        "don't close with a statement of fact.",
      part: "hook",
    });
  }

  const text = [observed, pattern, hook]
    .map((v) => (v || "").trim())
    .filter(Boolean)
    .join(" ");
  return { text, violations, passes: violations.length === 0 };
}
